<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CloseCircleMemberCard from './CloseCircleMemberCard.vue';
import { useCloseCircleState } from './useCloseCircleState.js';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['viewAll', 'openMember', 'create']);

const { t } = useI18n();
const closeCircle = useCloseCircleState();

const members = computed(() => closeCircle.membersForContact(props.contactId));
const visibleMembers = computed(() => members.value.slice(0, 3));
const isLoading = computed(() => closeCircle.isLoading(props.contactId));
const error = computed(() => closeCircle.errorForContact(props.contactId));

const loadMembers = () => closeCircle.loadMembers(props.contactId);

onMounted(loadMembers);
watch(() => props.contactId, loadMembers);
</script>

<template>
  <section class="mt-3 flex flex-col bg-n-solid-1">
    <div class="flex items-center justify-between px-4 py-3">
      <div class="min-w-0">
        <h2 class="truncate text-sm font-semibold text-n-slate-12">
          {{ t('CLOSE_CIRCLE.TITLE') }}
        </h2>
        <p class="text-xs text-n-slate-10">
          {{ t('CLOSE_CIRCLE.MEMBER_COUNT', { count: members.length }) }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="members.length > 3"
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('viewAll')"
        >
          <span class="i-lucide-list size-3.5" />
          {{ t('CLOSE_CIRCLE.VIEW_ALL') }}
        </button>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('create')"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('CLOSE_CIRCLE.ADD_SHORT') }}
        </button>
      </div>
    </div>

    <div v-if="isLoading && !members.length" class="px-4 pb-5 pt-1">
      <p class="text-sm text-n-slate-11">
        {{ t('CLOSE_CIRCLE.LOADING') }}
      </p>
    </div>

    <div
      v-else-if="error && !members.length"
      class="flex flex-col gap-3 px-4 pb-5 pt-1"
    >
      <p class="text-sm text-n-ruby-11">
        {{ t('CLOSE_CIRCLE.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="flex w-fit items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
        @click="loadMembers"
      >
        <span class="i-lucide-refresh-cw size-3.5" />
        {{ t('CLOSE_CIRCLE.RETRY') }}
      </button>
    </div>

    <div v-else-if="visibleMembers.length" class="flex flex-col">
      <template v-for="(member, index) in visibleMembers" :key="member.id">
        <div v-if="index > 0" class="mx-4 h-px bg-n-weak" />
        <CloseCircleMemberCard
          :member="member"
          compact
          :flush-on-desktop="false"
          @open="emit('openMember', member)"
        />
      </template>
      <button
        v-if="members.length > visibleMembers.length"
        type="button"
        class="mx-4 my-3 flex w-fit items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
        @click="emit('viewAll')"
      >
        <span class="i-lucide-list size-3.5" />
        {{ t('CLOSE_CIRCLE.VIEW_ALL_COUNT', { count: members.length }) }}
      </button>
    </div>

    <div v-else class="flex flex-col gap-3 px-4 pb-5 pt-1">
      <p class="truncate text-sm text-n-slate-11">
        {{ t('CLOSE_CIRCLE.EMPTY_DESCRIPTION') }}
      </p>
    </div>
  </section>
</template>
